/**
 * Root-Cause Analysis - Step 2: Attribute Selection
 *
 * Select attributes from the previously selected object types
 */

import React, { useState, useEffect } from 'react';
import { makePostRequest } from '../../../services/apiClient';

const Step2_AttributeSelection = ({ activeFile, selectedObjectTypes, initialSelectedAttributes = [], onBack, onNext }) => {
  const [attributes, setAttributes] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState(
    () => new Set(initialSelectedAttributes.map(a => `${a.object_type}:${a.attribute_name}`))
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch attributes on mount
  useEffect(() => {
    fetchAttributeMetadata();
  }, [activeFile, selectedObjectTypes]);

  const fetchAttributeMetadata = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await makePostRequest('/rootcause/attributes', {
        file_type: activeFile,
        selected_object_types: selectedObjectTypes
      });

      console.log('Attribute Metadata:', response);
      setAttributes(response);
    } catch (err) {
      console.error('Error fetching attribute metadata:', err);
      setError(err.message || 'Failed to load attributes');
    } finally {
      setLoading(false);
    }
  };

  // Toggle attribute selection (using composite key: objectType:attributeName)
  const toggleAttribute = (objectType, attributeName) => {
    const compositeKey = `${objectType}:${attributeName}`;
    setSelectedAttributes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(compositeKey)) {
        newSet.delete(compositeKey);
      } else {
        newSet.add(compositeKey);
      }
      return newSet;
    });
  };

  // Select all attributes
  const selectAllAttributes = (attributeList) => {
    setSelectedAttributes((prev) => {
      const newSet = new Set(prev);
      attributeList.forEach((attr) => {
        const compositeKey = `${attr.source_label}:${attr.attribute_name}`;
        newSet.add(compositeKey);
      });
      return newSet;
    });
  };

  // Deselect all attributes
  const deselectAllAttributes = (attributeList) => {
    setSelectedAttributes((prev) => {
      const newSet = new Set(prev);
      attributeList.forEach((attr) => {
        const compositeKey = `${attr.source_label}:${attr.attribute_name}`;
        newSet.delete(compositeKey);
      });
      return newSet;
    });
  };

  const handleNext = () => {
    if (selectedAttributes.size > 0) {
      // Convert composite keys to attribute selection objects preserving object type
      const attributeSelections = Array.from(selectedAttributes).map(compositeKey => {
        const [objectType, attributeName] = compositeKey.split(':');
        return {
          object_type: objectType,
          attribute_name: attributeName
        };
      });

      onNext({
        selectedAttributes: attributeSelections
      });
    }
  };

  // Render error state
  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800 font-medium">Error</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={() => onBack()}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Go Back
        </button>
      </div>
    );
  }

  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-600">Loading attributes...</div>
      </div>
    );
  }

  // Flatten all attributes for display
  const allAttributes = [];

  // ONLY show object attributes for selected object types (no event attributes)
  if (attributes?.object_attributes) {
    Object.entries(attributes.object_attributes).forEach(([objType, attrList]) => {
      attrList.forEach((attr) => {
        allAttributes.push({
          ...attr,
          source_label: objType
        });
      });
    });
  }

  return (
    <div className="flex flex-col p-6">
      {/* Header */}
      <div className="mb-4 flex-shrink-0">
        <h2 className="text-xl font-semibold text-gray-800">Step 2: Select Attributes</h2>
        <p className="text-sm text-gray-600 mt-1">
          Attributes from selected object types. Selected: <span className="font-medium">{selectedAttributes.size}</span>
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedObjectTypes.map((objType) => (
            <span
              key={objType}
              className="inline-block px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-full"
            >
              {objType}
            </span>
          ))}
        </div>
        <button
          onClick={onBack}
          className="mt-2 text-sm text-blue-600 hover:underline"
        >
          ← Change Object Types
        </button>
      </div>

      {/* Select All / Deselect All Controls */}
      <div className="mb-2 flex items-center gap-4 flex-shrink-0">
        <button
          onClick={() => selectAllAttributes(allAttributes)}
          className="text-sm text-blue-600 hover:underline font-medium"
        >
          Select All Attributes
        </button>
        <button
          onClick={() => deselectAllAttributes(allAttributes)}
          className="text-sm text-red-600 hover:underline font-medium"
        >
          Clear Selection
        </button>
      </div>

      {/* Multi-Column Value Distribution Table with dynamic sizing */}
      <div className="overflow-auto border border-gray-300 rounded-lg mb-4" style={{ maxHeight: '500px' }}>
        {allAttributes.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No attributes found for selected object types.
          </div>
        ) : (
          <table className="w-full text-xs border-collapse">
            <thead className="bg-gray-100 sticky top-0 z-10">
              {/* Header Row 1: Attribute Names with Checkboxes */}
              <tr>
                {allAttributes.map((attr) => {
                  const compositeKey = `${attr.source_label}:${attr.attribute_name}`;
                  const isSelected = selectedAttributes.has(compositeKey);
                  return (
                    <th
                      key={`header-${attr.source_label}-${attr.attribute_name}`}
                      colSpan={3}
                      className={`px-2 py-3 text-left border-r border-gray-300 cursor-pointer transition-colors ${
                        isSelected ? 'bg-blue-200 hover:bg-blue-300' : 'hover:bg-gray-200'
                      }`}
                      onClick={() => toggleAttribute(attr.source_label, attr.attribute_name)}
                    >
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleAttribute(attr.source_label, attr.attribute_name)}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-0.5 cursor-pointer"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-800 truncate" title={attr.attribute_name}>
                            {attr.attribute_name}
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5">
                            <span className="inline-block px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                              {attr.source_label}
                            </span>
                            <span className="ml-1 text-gray-500">
                              ({attr.cardinality} unique values)
                            </span>
                          </div>
                        </div>
                      </div>
                    </th>
                  );
                })}
              </tr>

              {/* Header Row 2: Value, Count, Share Sub-columns */}
              <tr className="bg-gray-50">
                {allAttributes.map((attr) => (
                  <React.Fragment key={`subheader-${attr.source_label}-${attr.attribute_name}`}>
                    <th className="px-2 py-2 text-left border-r border-b border-gray-300 font-medium text-gray-700">
                      Value (Top 20)
                    </th>
                    <th className="px-2 py-2 text-right border-r border-b border-gray-300 font-medium text-gray-700">
                      Count
                    </th>
                    <th className="px-2 py-2 text-right border-r border-b border-gray-300 font-medium text-gray-700">
                      %
                    </th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>

            <tbody>
              {/* Generate up to 20 rows for top values */}
              {Array.from({ length: 20 }).map((_, rowIndex) => (
                <tr key={`row-${rowIndex}`} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  {allAttributes.map((attr) => {
                    const detailedValues = attr.top_values_detailed || [];
                    const valueData = detailedValues[rowIndex];

                    return (
                      <React.Fragment key={`cell-${attr.source_label}-${attr.attribute_name}-${rowIndex}`}>
                        <td className="px-2 py-1.5 border-r border-b border-gray-200 text-gray-700 truncate max-w-[150px]" title={valueData?.value || ''}>
                          {valueData?.value || '-'}
                        </td>
                        <td className="px-2 py-1.5 border-r border-b border-gray-200 text-right text-gray-600">
                          {valueData ? valueData.count.toLocaleString() : '-'}
                        </td>
                        <td className="px-2 py-1.5 border-r border-b border-gray-200 text-right text-gray-600">
                          {valueData ? `${valueData.share.toFixed(1)}%` : '-'}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer Actions */}
      <div className="flex justify-between items-center flex-shrink-0">
        <button
          onClick={onBack}
          className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
        >
          ← Back
        </button>

        <div className="flex items-center gap-4">
          <button
            onClick={handleNext}
            disabled={selectedAttributes.size === 0}
            className={`px-6 py-2 rounded font-medium ${
              selectedAttributes.size > 0
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step2_AttributeSelection;
